import { gradientPointAt, rgb8ToHex, type GradientEndpoints, type Rgb8 } from "@hunch/core";
import styles from "./HuessworkBoxes.module.css";

const BOX_COUNT = 7;
const MIDDLE_INDEX = 3;

/** t-position (0..1 along the real A→B gradient) for box `index`, spread
    symmetrically around t=0.5 across `spanPercent`% of the full gradient —
    e.g. spanPercent=20 puts the 7 boxes between t=0.4 and t=0.6. */
function boxT(index: number, spanPercent: number): number {
  const span = spanPercent / 100;
  const step = span / (BOX_COUNT - 1);
  return 0.5 + (index - MIDDLE_INDEX) * step;
}

export interface HuessworkBoxesProps {
  endpoints: GradientEndpoints;
  /** The 7 boxes are spread across this % of the full gradient, centered at t=0.5. */
  spanPercent: number;
  /** While guessing: the live-mixed preview color shown in the middle box. */
  liveColor?: Rgb8 | undefined;
  /** Reveal mode: show a marker above the middle box. */
  revealed?: boolean;
  /** Reveal mode only: substitutes the player's guess for the middle box's
      real color — the 6 context boxes always stay the real sampled curve,
      never recomputed through the guess (that produced a misleadingly
      different-looking "spectrum" — 2026-10 fix). */
  altMiddle?: Rgb8 | undefined;
}

/**
 * Spec §9.6: the boxes sit on a neutral gray surface (#cfcfcf) with no other
 * colored elements nearby, so the surroundings don't bias the color
 * comparison. Replaces the earlier continuous-gradient-with-a-gap view: the
 * player now sees 7 discrete color boxes sampled along the gradient (3 each
 * side for context) and guesses only the middle one, per 2026-10 feedback.
 */
export function HuessworkBoxes({ endpoints, spanPercent, liveColor, revealed, altMiddle }: HuessworkBoxesProps) {
  return (
    <div className={styles.surface}>
      <div className={styles.rowWrapper}>
        {revealed && <div className={styles.middleArrow} aria-hidden />}
        <div className={styles.row}>
          {Array.from({ length: BOX_COUNT }, (_, i) => {
            const isMiddle = i === MIDDLE_INDEX;
            const real = gradientPointAt(endpoints, boxT(i, spanPercent));
            let color: Rgb8 | undefined = real;
            if (isMiddle) {
              if (!revealed) color = liveColor;
              else if (altMiddle) color = altMiddle;
            }
            return (
              <div key={i} className={styles.box} style={color ? { backgroundColor: rgb8ToHex(color) } : undefined} />
            );
          })}
        </div>
      </div>
    </div>
  );
}
